import { t } from "@lingui/macro";
import {
  Article,
  Books,
  Briefcase,
  CaretRight,
  Certificate,
  CheckCircle,
  GameController,
  GraduationCap,
  HandHeart,
  Medal,
  Palette,
  Plus,
  PuzzlePiece,
  ShareNetwork,
  Sparkle,
  Translate,
  User,
  Users,
  WarningCircle,
} from "@phosphor-icons/react";
import { ScrollArea } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";

import { useResumeStore } from "@/client/stores/resume";

const SECTION_ICONS: Record<string, React.ElementType> = {
  basics: User,
  summary: Article,
  profiles: ShareNetwork,
  experience: Briefcase,
  education: GraduationCap,
  projects: PuzzlePiece,
  skills: Palette,
  languages: Translate,
  certifications: Certificate,
  awards: Medal,
  publications: Books,
  volunteer: HandHeart,
  interests: GameController,
  hobbies: GameController,
  references: Users,
};

const SECTION_NAMES: Record<string, () => string> = {
  basics: () => t`Basics`,
  summary: () => t`Summary`,
  profiles: () => t`Profiles`,
  experience: () => t`Experience`,
  education: () => t`Education`,
  projects: () => t`Projects`,
  skills: () => t`Skills`,
  languages: () => t`Languages`,
  certifications: () => t`Certifications`,
  awards: () => t`Awards`,
  publications: () => t`Publications`,
  volunteer: () => t`Volunteer`,
  interests: () => t`Interests`,
  hobbies: () => t`Hobbies`,
  references: () => t`References`,
};

// Per-section icon background + text color
const SECTION_COLORS: Record<string, { bg: string; color: string }> = {
  basics:       { bg: "rgba(10,22,40,0.08)",    color: "#0A1628" },
  summary:      { bg: "rgba(139,92,246,0.10)",  color: "#7c3aed" },
  profiles:     { bg: "rgba(6,182,212,0.10)",   color: "#0891b2" },
  experience:   { bg: "rgba(38,133,153,0.10)",  color: "#268599" },
  education:    { bg: "rgba(99,102,241,0.10)",  color: "#4f46e5" },
  skills:       { bg: "rgba(249,115,22,0.10)",  color: "#ea580c" },
  languages:    { bg: "rgba(16,185,129,0.10)",  color: "#059669" },
  projects:     { bg: "rgba(168,85,247,0.10)",  color: "#9333ea" },
  certifications:{ bg: "rgba(245,158,11,0.10)", color: "#d97706" },
  awards:       { bg: "rgba(234,179,8,0.10)",   color: "#ca8a04" },
  publications: { bg: "rgba(59,130,246,0.10)",  color: "#2563eb" },
  volunteer:    { bg: "rgba(244,63,94,0.10)",   color: "#e11d48" },
  interests:    { bg: "rgba(132,204,22,0.10)",  color: "#65a30d" },
  hobbies:      { bg: "rgba(236,72,153,0.10)",  color: "#db2777" },
  references:   { bg: "rgba(20,184,166,0.10)",  color: "#0d9488" },
};

const SECTION_ORDER = [
  "basics", "summary", "experience", "education", "skills",
  "languages", "projects", "certifications", "awards",
  "publications", "volunteer", "interests", "hobbies", "references",
];

type BadgeVariant = "missing" | "ok" | "ai" | "add" | null;

interface SectionMeta { label: string; badge: BadgeVariant; }

const getSectionMeta = (sectionId: string, resumeData: any): SectionMeta => {
  try {
    if (sectionId === "basics") {
      const basics = resumeData?.basics ?? {};
      const required = ["name", "headline", "email"];
      const filled = required.filter((f: string) => basics[f]?.trim?.()).length;
      const total = Object.keys(basics).filter((k: string) => basics[k]).length;
      return { label: t`${total} of 6 fields`, badge: filled < required.length ? "missing" : "ok" };
    }
    if (sectionId === "summary") {
      const content: string = resumeData?.sections?.summary?.content ?? "";
      const words = content.trim() ? content.trim().split(/\s+/).length : 0;
      return { label: t`${words} / 80 words`, badge: words === 0 ? "add" : words >= 20 ? "ok" : "missing" };
    }
    const section = resumeData?.sections?.[sectionId];
    if (!section) return { label: t`Empty`, badge: "add" };
    const items: any[] = section.items ?? [];
    if (items.length === 0) return { label: t`Empty`, badge: "add" };
    if (sectionId === "experience") {
      const bullets = items.reduce((s: number, r: any) => s + (r.bullets?.length ?? 0), 0);
      return { label: t`${items.length} roles · ${bullets} bullets`, badge: items.some((r: any) => r.aiTailored) ? "ai" : "ok" };
    }
    if (sectionId === "skills") return { label: t`${items.length} tags`, badge: items.length > 0 ? "ok" : "add" };
    return { label: t`${items.length} entries`, badge: items.length > 0 ? "ok" : "add" };
  } catch {
    return { label: t`Empty`, badge: "add" };
  }
};

type MobileNavigationProps = {
  onSectionSelect: (sectionId: string) => void;
  activeSectionId?: string;
  className?: string;
};

export const MobileNavigation = ({ onSectionSelect, activeSectionId, className }: MobileNavigationProps) => {
  const resumeData = useResumeStore((state) => { try { return state.resume.data; } catch { return null; } });
  const jobTitle: string | undefined = (resumeData as any)?.metadata?.jobTitle;

  return (
    <div className={cn("flex h-full flex-col bg-[#faf9f5]", className)}>
      <ScrollArea className="flex-1">
        <div className="px-4 pb-8 pt-3 space-y-2.5">

          {/* ATS strip */}
          {jobTitle && (
            <div className="flex items-center gap-3 rounded-2xl border border-[#268599]/20 bg-[#268599]/[0.06] px-4 py-3 mb-1">
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-bold uppercase tracking-widest text-[#aaa]">{t`Tailored for`}</p>
                <p className="truncate text-[12px] font-bold text-[#0A1628]">{jobTitle}</p>
              </div>
              <span className="text-[22px] font-extrabold text-[#268599]">
                {(resumeData as any)?.metadata?.atsScore ?? "–"}%
              </span>
            </div>
          )}

          {/* Section cards */}
          {SECTION_ORDER.map((sectionId) => {
            const Icon = SECTION_ICONS[sectionId] ?? User;
            const { label, badge } = getSectionMeta(sectionId, resumeData);
            const isActive = activeSectionId === sectionId;
            const colors = SECTION_COLORS[sectionId] ?? { bg: "rgba(38,133,153,0.10)", color: "#268599" };

            return (
              <button
                key={sectionId}
                onClick={() => onSectionSelect(sectionId)}
                className={cn(
                  "group flex w-full items-center gap-3.5 rounded-2xl border bg-white px-4 py-3.5 text-left transition-all active:scale-[0.99]",
                  isActive
                    ? "border-[#268599]/30 shadow-[0_0_0_3px_rgba(38,133,153,0.08)]"
                    : "border-black/[0.05] shadow-[0_1px_4px_rgba(10,22,40,0.06)]",
                )}
              >
                {/* Icon box */}
                <div
                  className="flex size-10 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: colors.bg }}
                >
                  <Icon size={18} style={{ color: colors.color }} weight="duotone" />
                </div>

                {/* Text */}
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-bold text-[#0A1628] leading-tight">
                    {SECTION_NAMES[sectionId]?.() ?? sectionId}
                  </p>
                  <p className="mt-[3px] text-[11px] text-[#9ca3af] leading-tight truncate">
                    {label}
                  </p>
                </div>

                {/* Status */}
                <StatusBadge badge={badge} />

                <CaretRight size={13} className="shrink-0 text-[#d4d4d4] transition-transform group-active:translate-x-0.5" />
              </button>
            );
          })}

          {/* Add custom section */}
          <button
            onClick={() => {/* handled by parent */}}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#d4d4d4] bg-transparent py-3.5 text-[12px] font-semibold text-[#9ca3af] transition-colors hover:border-[#268599]/40 hover:text-[#268599]"
          >
            <Plus size={14} weight="bold" />
            {t`Add custom section`}
          </button>
        </div>
      </ScrollArea>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------
const StatusBadge = ({ badge }: { badge: BadgeVariant }) => {
  if (!badge) return null;

  if (badge === "ok") return (
    <CheckCircle size={18} weight="fill" className="shrink-0" style={{ color: "#22c55e" }} />
  );

  if (badge === "missing") return (
    <WarningCircle size={18} weight="fill" className="shrink-0" style={{ color: "#e8885e" }} />
  );

  if (badge === "ai") return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3, flexShrink: 0,
      background: "rgba(38,133,153,0.10)", color: "#268599",
      borderRadius: 6, padding: "2px 6px",
      fontSize: 9, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
    }}>
      <Sparkle size={9} weight="fill" />
      AI
    </span>
  );

  // add
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3, flexShrink: 0,
      background: "#f5f5f5", color: "#aaa",
      borderRadius: 6, padding: "2px 7px",
      fontSize: 9, fontWeight: 700, letterSpacing: "0.04em",
    }}>
      Empty
    </span>
  );
};
